import { Calendar, Copy, Edit, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import type { EventTag } from '@goenhub/shared';

import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAuth } from '@/hooks/useAuth';
import {
  useDeleteEvent,
  useDuplicateEvent,
  useEvents,
  type EventRecord,
} from '@/hooks/useLineData';
import { formatDateTime } from '@/lib/firestoreTime';

import { EventFormDialog } from './EventFormDialog';

function tagBadge(tag: EventTag | undefined) {
  if (!tag) return null;
  const label = { online: 'オンライン', onsite: '会場', both: 'ハイブリッド' }[tag];
  const variant = tag === 'online' ? 'accent' : tag === 'onsite' ? 'approved' : 'review';
  return <Badge variant={variant}>{label}</Badge>;
}

export function EventsPage() {
  const { user } = useAuth();
  const canEdit = user?.role === 'owner' || user?.role === 'editor';
  const { data, isLoading } = useEvents();
  const deleteMutation = useDeleteEvent();
  const duplicateMutation = useDuplicateEvent();
  const [editing, setEditing] = useState<EventRecord | null>(null);
  const [creating, setCreating] = useState(false);

  const handleDelete = async (ev: EventRecord) => {
    if (!window.confirm(`イベント「${ev.name}」を削除します。よろしいですか？`)) return;
    try {
      await deleteMutation.mutateAsync(ev.id);
      toast.success('イベントを削除しました');
    } catch (err) {
      toast.error(`削除に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  const handleDuplicate = async (ev: EventRecord) => {
    try {
      await duplicateMutation.mutateAsync(ev.id);
      toast.success('イベントを複製しました');
    } catch (err) {
      toast.error(`複製に失敗しました: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  return (
    <Screen
      title="イベント管理"
      description="LINE Bot が参照するイベント情報を管理します"
      actions={
        canEdit && (
          <Button type="button" variant="primary" onClick={() => setCreating(true)}>
            <Plus />
            新規作成
          </Button>
        )
      }
    >
      {isLoading ? (
        <Spinner />
      ) : data && data.length > 0 ? (
        <div className="rounded bg-[var(--bg-surface)] border border-[var(--border)] overflow-hidden">
          {data.map((ev) => (
            <div
              key={ev.id}
              className="flex items-center gap-4 p-4 border-b border-[var(--border)] last:border-b-0 hover:bg-[var(--bg-hover)] transition-colors"
            >
              <Calendar className="size-4 text-[var(--text-muted)] flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-[13px] truncate">{ev.name}</span>
                  {tagBadge(ev.tag)}
                  {!ev.publishedAt && <Badge variant="danger">非公開</Badge>}
                </div>
                <div className="text-xs text-[var(--text-muted)] flex items-center gap-3">
                  <span>{formatDateTime(ev.eventDate)}</span>
                  <span>
                    定員: {ev.capacity || '無制限'} / 現在 {ev.currentAttendees}
                  </span>
                  {ev.participantCode && (
                    <code className="font-mono bg-[var(--bg-hover)] px-1.5 py-0.5 rounded text-[10px]">
                      code: {ev.participantCode}
                    </code>
                  )}
                </div>
              </div>
              {canEdit && (
                <>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => setEditing(ev)}
                    title="編集"
                  >
                    <Edit />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDuplicate(ev)}
                    disabled={duplicateMutation.isPending}
                    title="複製"
                  >
                    <Copy />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(ev)}
                    title="削除"
                  >
                    <Trash2 />
                  </Button>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={Calendar}
          title="イベントが登録されていません"
          description="「新規作成」から登録できます"
        />
      )}

      <EventFormDialog
        open={creating || !!editing}
        existing={editing}
        onOpenChange={(o) => {
          if (!o) {
            setCreating(false);
            setEditing(null);
          }
        }}
      />
    </Screen>
  );
}
